import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Card } from 'react-bootstrap';
import { toast } from 'react-toastify';
import moment from 'moment';
import Header from '~/Components/Header/Header';
import { extrairMensagemErro } from '~/Utils/Helpers';
import {
  filtrarImoveis,
  lookupFamilias,
  lookupGruposGe,
  lookupGruposEmpresa,
  lookupCidades,
  lookupCarneStatus,
  lookupStatusImovel,
  lookupStatusDebitoIPTU,
  lookupOpcoesIPTU,
  exportarExcelComMeta,
  consultarProgressoExcel,
  baixarCalculoRescisaoPdf,
} from '~/Services/GestaoIPTUService';

import FiltroGestaoIPTU from './components/FiltroGestaoIPTU';
import GridGestaoIPTU from './components/GridGestaoIPTU';
import ModalAlterarCarneStatus from './components/ModalAlterarCarneStatus';
import ModalAlterarOpcaoPagamento from './components/ModalAlterarOpcaoPagamento';
import ModalAlterarOpcaoCondominio from './components/ModalAlterarOpcaoCondominio';
import ModalAlterarCadPrefeitura from './components/ModalAlterarCadPrefeitura';
import ModalAlterarOrdemPagamento from './components/ModalAlterarOrdemPagamento';
import ModalDetalhesImovel from './components/ModalDetalhesImovel';
import ModalParametrosExcel from './components/ModalParametrosExcel';
import ModalExportEtapa from './components/ModalExportEtapa';
import ModalExcelGerado from './components/ModalExcelGerado';
import ModalCadContaPrefeitura from './components/ModalCadContaPrefeitura';
import ModalDebitosIPTU from './components/ModalDebitosIPTU';
import ModalSelContrato from './components/ModalSelContrato';

const FILTROS_INICIAIS = {
  // Empreendimento agora é multi-select (Melhoria filtros W-0718).
  codigoFamilia: [],
  grupoGe: 10,
  // Novo filtro GE (Melhoria filtros W-0718) — mutuamente exclusivo com grupoGe.
  ge: null,
  codigoCidade: null,
  quadra: '',
  lote: '',
  dataVendaInicio: null,
  dataVendaFim: null,
  statusImovel: [],
  statusCarne: [],
  statusDebitoIptu: null,
  ultimaRescisaoInicio: null,
  ultimaRescisaoFim: null,
  filtroOrdemPagamento: -1,
  filtroOpcaoPagamento: -1,
  filtroOpcaoCondominio: -1,
  cadPrefeitura: '',
  tiposMov: [1, 2, 3, 4, 5],
  codigoAtivo: '',
};

const GestaoIPTU = () => {
  const [filtros, setFiltros] = useState(FILTROS_INICIAIS);
  const [imoveis, setImoveis] = useState([]);
  const [selecionados, setSelecionados] = useState([]);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [carregando, setCarregando] = useState(false);
  const [carregandoExcel, setCarregandoExcel] = useState(false);
  const [modalAtivo, setModalAtivo] = useState(null);
  const [imovelDetalhes, setImovelDetalhes] = useState(null);
  const [mostrarModalExcel, setMostrarModalExcel] = useState(false);
  const [mostrarModalCadPref, setMostrarModalCadPref] = useState(false);
  const [mostrarModalDebitos, setMostrarModalDebitos] = useState(false);
  const [rescisaoSelContrato, setRescisaoSelContrato] = useState(null);
  const [gerandoPdfRescisao, setGerandoPdfRescisao] = useState(false);

  // D2 — Baixa o PDF do cálculo de rescisão. Réplica do
  // ImprimeCalculoRescisao do Delphi (UImovelCarne.pas:768-1014) — em vez de
  // abrir preview, gera o arquivo direto (Content-Disposition: attachment
  // no backend, ver GestaoIPTUController.CalculoRescisaoPdf).
  const baixarPdfRescisao = async (codigoAtivo, codigoBoleto) => {
    setGerandoPdfRescisao(true);
    try {
      const blob = await baixarCalculoRescisaoPdf(codigoAtivo, codigoBoleto);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const suffix = codigoBoleto ? `_${codigoBoleto}` : '';
      link.download = `CalculoRescisao_${codigoAtivo}${suffix}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(extrairMensagemErro(err) || 'Não foi possível gerar o PDF do cálculo de rescisão.');
    } finally {
      setGerandoPdfRescisao(false);
    }
  };
  const [ordenacao, setOrdenacao] = useState({ campo: 'empreendimento', direcao: 'asc' });

  const [familias, setFamilias] = useState([]);
  const [gruposGe, setGruposGe] = useState([]);
  const [gruposEmpresa, setGruposEmpresa] = useState([]);
  const [cidades, setCidades] = useState([]);
  const [carneStatus, setCarneStatus] = useState([]);
  const [statusImovelOpcoes, setStatusImovelOpcoes] = useState([]);
  const [statusDebitoIPTUOpcoes, setStatusDebitoIPTUOpcoes] = useState([]);
  const [opcoesIPTU, setOpcoesIPTU] = useState([]);
  const [habilitaCondominio, setHabilitaCondominio] = useState(false);

  useEffect(() => {
    let ativo = true;
    // Cidades não entram no carregamento inicial: dependem de Grupo GE / GE
    // (Melhoria filtros W-0718). O effect dedicado abaixo recarrega quando o
    // escopo muda. No 1º render, o FILTROS_INICIAIS já vem com grupoGe=10
    // setado, então o effect dispara o lookup antes do usuário interagir.
    Promise.all([
      lookupFamilias().catch(() => []),
      lookupGruposGe().catch(() => []),
      lookupGruposEmpresa().catch(() => []),
      lookupCarneStatus().catch(() => []),
      lookupStatusImovel().catch(() => []),
      lookupStatusDebitoIPTU().catch(() => []),
      lookupOpcoesIPTU().catch(() => []),
    ]).then(([fams, gruposGeResp, gruposEmpResp, carneSt, statusIm, statusDeb, opcoes]) => {
      if (!ativo) return;
      setFamilias(fams || []);
      setGruposGe(gruposGeResp || []);
      setGruposEmpresa(gruposEmpResp || []);
      setCarneStatus(carneSt || []);
      setStatusImovelOpcoes(statusIm || []);
      setStatusDebitoIPTUOpcoes(statusDeb || []);
      setOpcoesIPTU(opcoes || []);
    });
    return () => { ativo = false; };
  }, []);

  // Cidades dependentes de Grupo GE / GE (Melhoria filtros W-0718).
  // Sem nenhum dos dois → lista vazia (regra verbatim do docx — backend já
  // devolve [], mas zeramos no FE pra evitar piscar opções antigas).
  useEffect(() => {
    let ativo = true;
    if (!filtros.grupoGe && !filtros.ge) {
      setCidades([]);
      // Limpa cidade selecionada se ficou sem escopo.
      if (filtros.codigoCidade) {
        setFiltros((prev) => ({ ...prev, codigoCidade: null }));
      }
      return undefined;
    }
    const params = {};
    if (filtros.grupoGe) params.grupoGe = filtros.grupoGe;
    if (filtros.ge) params.ge = filtros.ge;
    lookupCidades(params)
      .then((lista) => {
        if (!ativo) return;
        // Defesa contra registros lixo de Tab_Cidades (Descricao_Cidade NULL ou
        // vazia) que escapem do filtro do BE — evita renderizar opção em branco
        // no react-select (faixa azul sem texto).
        const cids = (Array.isArray(lista) ? lista : []).filter(
          (c) => c && c.descricao && String(c.descricao).trim() !== ''
        );
        setCidades(cids);
        // Se a cidade atualmente selecionada não está mais no novo escopo, limpa.
        if (filtros.codigoCidade && !cids.some((c) => c.codigo === filtros.codigoCidade)) {
          setFiltros((prev) => ({ ...prev, codigoCidade: null }));
        }
      })
      .catch(() => { if (ativo) setCidades([]); });
    return () => { ativo = false; };
  }, [filtros.grupoGe, filtros.ge]); // eslint-disable-line react-hooks/exhaustive-deps

  // Empreendimento agora é multi-select — o botão Opção Cond depende de
  // pelo menos um empreendimento marcado ser V/H (decisão prática: habilita
  // se TODOS os selecionados forem V/H, evita estado ambíguo no UPDATE em lote).
  useEffect(() => {
    if (!filtros.codigoFamilia || filtros.codigoFamilia.length === 0) {
      setHabilitaCondominio(false);
      return;
    }
    const selecionadasInfo = filtros.codigoFamilia
      .map((cod) => familias.find((f) => f.codigo === cod))
      .filter(Boolean);
    if (selecionadasInfo.length === 0) {
      setHabilitaCondominio(false);
      return;
    }
    setHabilitaCondominio(selecionadasInfo.every((f) => f.habilita_condominio === true));
  }, [filtros.codigoFamilia, familias]);

  useEffect(() => {
    if (!habilitaCondominio && filtros.filtroOpcaoCondominio !== -1) {
      setFiltros((prev) => ({ ...prev, filtroOpcaoCondominio: -1 }));
    }
  }, [habilitaCondominio]);  // eslint-disable-line react-hooks/exhaustive-deps

  const montarParams = useCallback(() => ({
    // Empreendimento multi-select (Melhoria filtros W-0718) — envia array
    // ou omite. paramsSerializer do GestaoIPTUService.js repete a chave (?codigoFamilia=1&codigoFamilia=2).
    codigoFamilia: filtros.codigoFamilia && filtros.codigoFamilia.length > 0
      ? filtros.codigoFamilia : undefined,
    grupoGe: filtros.grupoGe || undefined,
    // Novo filtro GE (Melhoria filtros W-0718).
    ge: filtros.ge || undefined,
    codigoCidade: filtros.codigoCidade || undefined,
    quadra: filtros.quadra || undefined,
    lote: filtros.lote || undefined,
    dataVendaInicio: filtros.dataVendaInicio
      ? moment(filtros.dataVendaInicio).format('YYYY-MM-DD')
      : undefined,
    dataVendaFim: filtros.dataVendaFim
      ? moment(filtros.dataVendaFim).format('YYYY-MM-DD')
      : undefined,
    statusImovel: filtros.statusImovel && filtros.statusImovel.length > 0 ? filtros.statusImovel : undefined,
    statusCarne: filtros.statusCarne && filtros.statusCarne.length > 0 ? filtros.statusCarne : undefined,
    statusDebitoIptu: filtros.statusDebitoIptu || undefined,
    ultimaRescisaoInicio: filtros.ultimaRescisaoInicio
      ? moment(filtros.ultimaRescisaoInicio).format('YYYY-MM-DD')
      : undefined,
    ultimaRescisaoFim: filtros.ultimaRescisaoFim
      ? moment(filtros.ultimaRescisaoFim).format('YYYY-MM-DD')
      : undefined,
    filtroOrdemPagamento: filtros.filtroOrdemPagamento === -1 ? undefined : filtros.filtroOrdemPagamento,
    filtroOpcaoPagamento: filtros.filtroOpcaoPagamento === -1 ? undefined : filtros.filtroOpcaoPagamento,
    filtroOpcaoCondominio: filtros.filtroOpcaoCondominio === -1 ? undefined : filtros.filtroOpcaoCondominio,
    cadPrefeitura: (filtros.cadPrefeitura && filtros.cadPrefeitura.trim()) || undefined,
    tiposMov: filtros.tiposMov,
    codigoAtivo: filtros.codigoAtivo ? parseInt(filtros.codigoAtivo, 10) : undefined,
  }), [filtros]);

  const validarFiltros = () => {
    const temFamilia = filtros.codigoFamilia && filtros.codigoFamilia.length > 0;
    if (!temFamilia && !filtros.grupoGe && !filtros.ge) {
      toast.warning('Selecione um empreendimento, um Grupo GE ou um GE.');
      return false;
    }
    if (!filtros.dataVendaInicio || !filtros.dataVendaFim) {
      toast.warning('Informe as datas de venda (De e Até).');
      return false;
    }
    if (!filtros.tiposMov || filtros.tiposMov.length === 0) {
      toast.error('Nenhum tipo selecionado');
      return false;
    }
    return true;
  };

  const buscar = async (pag) => {
    if (!validarFiltros()) return;
    setCarregando(true);
    try {
      const params = montarParams();
      const resp = await filtrarImoveis(params);
      const lista = (resp && resp.dados) || [];
      const total = lista.length;
      setImoveis(lista);
      setTotalRegistros(total);
      setPagina(pag || 1);
      setSelecionados([]);
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    } finally {
      setCarregando(false);
    }
  };

  const handleBuscar = () => buscar(1);

  const handleLimpar = () => {
    setFiltros(FILTROS_INICIAIS);
    setImoveis([]);
    setTotalRegistros(0);
    setPagina(1);
    setSelecionados([]);
  };

  const handleMudarPagina = (pag) => {
    setSelecionados([]);
    setPagina(pag);
  };

  const handleOrdenacao = (campo) => {
    setOrdenacao((prev) => ({
      campo,
      direcao: prev.campo === campo && prev.direcao === 'asc' ? 'desc' : 'asc',
    }));
  };

  const imoveisOrdenados = [...imoveis].sort((a, b) => {
    const dir = ordenacao.direcao === 'asc' ? 1 : -1;
    const campo = ordenacao.campo;
    const vA = a[campo];
    const vB = b[campo];
    if (vA == null && vB == null) return 0;
    if (vA == null) return dir;
    if (vB == null) return -dir;
    if (typeof vA === 'number') return dir * (vA - vB);
    return dir * String(vA).localeCompare(String(vB));
  });

  const tamanhoPagina = 50;
  const inicioPagina = (pagina - 1) * tamanhoPagina;
  const imoveisPaginados = imoveisOrdenados.slice(inicioPagina, inicioPagina + tamanhoPagina);

  const handleToggleSelecionado = (codigoAtivo) => {
    setSelecionados((prev) =>
      prev.includes(codigoAtivo)
        ? prev.filter((c) => c !== codigoAtivo)
        : [...prev, codigoAtivo]
    );
  };

  const handleToggleTodos = () => {
    const todosSelecionados = imoveisPaginados.every((i) => selecionados.includes(i.codigo_ativo));
    if (todosSelecionados) {
      setSelecionados((prev) => prev.filter((c) => !imoveisPaginados.some((i) => i.codigo_ativo === c)));
    } else {
      setSelecionados((prev) => Array.from(new Set([
        ...prev,
        ...imoveisPaginados.map((i) => i.codigo_ativo),
      ])));
    }
  };

  const handleAcaoConcluida = () => {
    buscar(pagina);
    setSelecionados([]);
  };

  // D6 — abre modal de parâmetros antes do download.
  const handleAbrirModalExcel = () => {
    if (!validarFiltros()) return;
    setMostrarModalExcel(true);
  };

  const [excelGerado, setExcelGerado] = useState(null); // { url, nomeArquivo }
  const [etapaExcel, setEtapaExcel] = useState(null); // string com a fase atual
  const progressoIntervalRef = useRef(null);

  const pararPollingEtapa = () => {
    if (progressoIntervalRef.current) {
      clearInterval(progressoIntervalRef.current);
      progressoIntervalRef.current = null;
    }
  };

  useEffect(() => () => pararPollingEtapa(), []); // cleanup ao desmontar

  const handleConfirmarExcel = async (parametros) => {
    setCarregandoExcel(true);
    setMostrarModalExcel(false);

    // Polling pra acompanhar a etapa atual do backend (Buscando imóveis →
    // Buscando fornecedores → Gerando arquivo Excel → Finalizando).
    // Substitui a barra de progresso por feedback de fase (sem contagem).
    const progressId = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : 'iptu-' + Date.now() + '-' + Math.random().toString(36).slice(2);
    setEtapaExcel('Iniciando…');
    pararPollingEtapa();
    progressoIntervalRef.current = setInterval(async () => {
      try {
        const resp = await consultarProgressoExcel(progressId);
        if (resp && resp.etapa) setEtapaExcel(resp.etapa);
      } catch (_) {
        // Polling falhou — ignora (export pode estar terminando).
      }
    }, 300);

    try {
      // Backend usa SnakeCaseNamingStrategy global (Setpar.APIRest/Startup.cs:53),
      // que afeta deserialize do [FromBody]. Convertemos camelCase → snake_case.
      const camelToSnake = (s) => s.replace(/([A-Z])/g, '_$1').toLowerCase();
      const toSnake = (v) => {
        if (v === null || v === undefined || typeof v !== 'object') return v;
        if (Array.isArray(v)) return v.map(toSnake);
        const out = {};
        Object.keys(v).forEach((k) => {
          if (v[k] !== undefined) out[camelToSnake(k)] = toSnake(v[k]);
        });
        return out;
      };
      const payload = toSnake({ ...montarParams(), ...parametros, progressId });
      const resultado = await exportarExcelComMeta(payload);

      pararPollingEtapa();
      setEtapaExcel(null);

      if (resultado.modo === 'multiplos') {
        // ArquivoUnico=false — N downloads consecutivos, 1 .xlsx por
        // empreendimento (replica UImovelCarne.pas:725-731). Browser pode
        // pedir permissão "permitir múltiplos downloads neste site" no
        // primeiro uso — usuário confirma uma vez por sessão.
        const lista = resultado.arquivos || [];
        for (let i = 0; i < lista.length; i += 1) {
          const arq = lista[i];
          const bytes = Uint8Array.from(atob(arq.conteudoBase64), (c) => c.charCodeAt(0));
          const blob = new Blob([bytes], { type: arq.contentType || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = arq.nomeArquivo;
          link.click();
          link.remove();
          // Revoga URL imediato — não dá pra reusar via "Baixar novamente" em modo múltiplo.
          window.URL.revokeObjectURL(url);
          // Pausa entre downloads pra browser processar (Chrome/Edge bloqueiam disparos muito juntos).
          if (i < lista.length - 1) {
            // eslint-disable-next-line no-await-in-loop
            await new Promise((r) => setTimeout(r, 250));
          }
        }
        setExcelGerado({ multiplos: true, quantidade: lista.length });
      } else {
        // ArquivoUnico=true (ou fallback) — 1 .xlsx único.
        const { blob, nomeArquivo: nomeBack, contentType } = resultado;
        const dataNome = moment().format('DDMMYYYY');
        const despesa = parametros && parametros.despesa ? String(parametros.despesa).trim() : '';
        const nomeArquivo = nomeBack
          || (despesa ? `${despesa}-IPTU_${dataNome}.xlsx` : `gestao-iptu_${dataNome}.xlsx`);
        const mime = contentType || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        const url = window.URL.createObjectURL(new Blob([blob], { type: mime }));
        const link = document.createElement('a');
        link.href = url;
        link.download = nomeArquivo;
        link.click();
        link.remove();
        // Não revogamos a URL aqui — ela é reusada pelo botão "Baixar novamente"
        // do ModalExcelGerado e revogada quando o modal é fechado.
        setExcelGerado({ url, nomeArquivo });
      }
    } catch (err) {
      pararPollingEtapa();
      setEtapaExcel(null);
      toast.error(extrairMensagemErro(err));
    } finally {
      setCarregandoExcel(false);
    }
  };

  const handleFecharExcelGerado = () => {
    if (excelGerado && excelGerado.url) {
      window.URL.revokeObjectURL(excelGerado.url);
    }
    setExcelGerado(null);
  };

  const cidadeSelecionadaDescr = (() => {
    if (!filtros.codigoCidade) return null;
    const c = (cidades || []).find((x) => x.codigo === filtros.codigoCidade);
    return c ? c.descricao : String(filtros.codigoCidade);
  })();

  return (
    <Container fluid className="px-3 py-3">
      <Header title="Gestão IPTU" />

      <FiltroGestaoIPTU
        filtros={filtros}
        onChange={setFiltros}
        onBuscar={handleBuscar}
        onLimpar={handleLimpar}
        familias={familias}
        gruposGe={gruposGe}
        gruposEmpresa={gruposEmpresa}
        cidades={cidades}
        carneStatus={carneStatus}
        statusImovel={statusImovelOpcoes}
        statusDebitoIPTU={statusDebitoIPTUOpcoes}
        opcoesIPTU={opcoesIPTU}
        habilitaCondominio={habilitaCondominio}
        carregando={carregando}
      />

      <Card className="shadow-sm">
        <Card.Body className="p-0">
          <GridGestaoIPTU
            imoveis={imoveisPaginados}
            selecionados={selecionados}
            onToggleSelecionado={handleToggleSelecionado}
            onToggleTodos={handleToggleTodos}
            habilitaCondominio={habilitaCondominio}
            cidadeSelecionada={!!filtros.codigoCidade}
            onAbrirModal={setModalAtivo}
            onVerDetalhes={setImovelDetalhes}
            onDebitosIPTU={() => {
              if (!filtros.codigoCidade) {
                toast.warning('Necessário selecionar uma cidade.');
                return;
              }
              setMostrarModalDebitos(true);
            }}
            onCadPrefeitura={() => setMostrarModalCadPref(true)}
            onImprimirRescisao={(imovel) => {
              if (!imovel || !imovel.codigo_ativo) {
                toast.warning('Selecione um imóvel para imprimir o cálculo de rescisão.');
                return;
              }
              // UImovelCarne.pas:780-820 — MovTemRescisao='S' abre seleção
              // de contrato; caso contrário, vai direto pro cálculo (o BE
              // resolve o último Boleto_RES do ativo).
              if (imovel.mov_tem_rescisao === 'S') {
                setRescisaoSelContrato({ codigoAtivo: imovel.codigo_ativo });
              } else {
                baixarPdfRescisao(imovel.codigo_ativo, null);
              }
            }}
            onExportarExcel={handleAbrirModalExcel}
            totalRegistros={totalRegistros}
            pagina={pagina}
            onMudarPagina={handleMudarPagina}
            carregando={carregando}
            carregandoExcel={carregandoExcel}
            ordenacao={ordenacao}
            onOrdenacao={handleOrdenacao}
          />
        </Card.Body>
      </Card>

      <ModalAlterarCarneStatus
        show={modalAtivo === 'carne-status'}
        onHide={() => setModalAtivo(null)}
        selecionados={selecionados}
        onSucesso={handleAcaoConcluida}
      />
      <ModalAlterarOpcaoPagamento
        show={modalAtivo === 'opcao-pagamento'}
        onHide={() => setModalAtivo(null)}
        selecionados={selecionados}
        onSucesso={handleAcaoConcluida}
      />
      <ModalAlterarOpcaoCondominio
        show={modalAtivo === 'opcao-condominio'}
        onHide={() => setModalAtivo(null)}
        selecionados={selecionados}
        onSucesso={handleAcaoConcluida}
      />
      <ModalAlterarCadPrefeitura
        show={modalAtivo === 'cad-prefeitura'}
        onHide={() => setModalAtivo(null)}
        selecionados={selecionados}
        cadPrefeituraAtual={(() => {
          // Replica Delphi (UImovelCarne.pas:Gprefeito.Tag = código do imóvel atual):
          // pré-popula o campo com cad_prefeitura quando há 1 imóvel selecionado;
          // se múltiplos com mesmo valor, mantém; senão deixa em branco.
          const imoveisSel = imoveis.filter((i) => selecionados.includes(i.codigo_ativo));
          if (imoveisSel.length === 0) return '';
          const valores = Array.from(new Set(imoveisSel.map((i) => i.cad_prefeitura || '')));
          return valores.length === 1 ? valores[0] : '';
        })()}
        onSucesso={handleAcaoConcluida}
      />
      <ModalAlterarOrdemPagamento
        show={modalAtivo === 'ordem-pagamento'}
        onHide={() => setModalAtivo(null)}
        selecionados={selecionados}
        onSucesso={handleAcaoConcluida}
      />

      <ModalDetalhesImovel
        show={imovelDetalhes !== null}
        onHide={() => setImovelDetalhes(null)}
        imovel={imovelDetalhes}
      />

      <ModalParametrosExcel
        show={mostrarModalExcel}
        onHide={() => setMostrarModalExcel(false)}
        onConfirmar={handleConfirmarExcel}
        carregando={carregandoExcel}
      />

      <ModalExportEtapa
        show={etapaExcel !== null}
        etapa={etapaExcel}
      />

      <ModalExportEtapa
        show={gerandoPdfRescisao}
        etapa="Gerando PDF do cálculo de rescisão..."
      />

      <ModalExcelGerado
        show={excelGerado !== null}
        onHide={handleFecharExcelGerado}
        nomeArquivo={excelGerado ? excelGerado.nomeArquivo : null}
        url={excelGerado ? excelGerado.url : null}
        multiplos={excelGerado ? !!excelGerado.multiplos : false}
        quantidade={excelGerado ? excelGerado.quantidade : 0}
      />

      <ModalCadContaPrefeitura
        show={mostrarModalCadPref}
        onHide={() => setMostrarModalCadPref(false)}
      />

      <ModalDebitosIPTU
        show={mostrarModalDebitos}
        onHide={() => setMostrarModalDebitos(false)}
        codigosImoveis={imoveis.map((i) => i.codigo_ativo)}
        cidadeSelecionada={cidadeSelecionadaDescr}
      />

      <ModalSelContrato
        show={rescisaoSelContrato !== null}
        onHide={() => setRescisaoSelContrato(null)}
        codigoAtivo={rescisaoSelContrato ? rescisaoSelContrato.codigoAtivo : null}
        onConfirmar={({ codigoBoleto }) => {
          const ativo = rescisaoSelContrato ? rescisaoSelContrato.codigoAtivo : null;
          setRescisaoSelContrato(null);
          if (ativo) baixarPdfRescisao(ativo, codigoBoleto);
        }}
      />
    </Container>
  );
};

export default GestaoIPTU;
